<template>
  <v-container v-if="fund" fluid>
    <v-app-bar app dark dense color="secondary">
      <v-toolbar-title>
        {{ fund.name }}
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <v-btn
        :to="{
          name: 'auditAuditPeriod',
          params: { id: fund.auditPeriod.id },
        }"
      >
        {{ $t("auditor.stepModelRequirements.back") }}
      </v-btn>
    </v-app-bar>

    <StepModelSuccess v-if="awaitingEdit" :fund="fund" />

    <template v-else>
      <v-row>
        <v-col cols="12">
          <AuditStatus :requirements="fund.stepModelRequirements" />
        </v-col>

        <v-col cols="12">
          <DataTable :fund="fund" />
        </v-col>

        <v-col cols="12">
          <v-card>
            <StepModelRequirementsSummaryTable :fund="{ report }" audit />
          </v-card>
        </v-col>
      </v-row>

      <v-row v-for="(category, index) in categories" :key="index">
        <v-col cols="12" class="d-flex justify-space-between align-center">
          <h3>
            {{ $t(`stepModelRequirementCategory.${index}.name`) }}
          </h3>

          <CategoryPoints :categories="reportCategories" :category="index" />
        </v-col>

        <v-col v-for="requirement in category" :key="requirement.id" cols="12">
          <StepModelRequirementForm :requirement="requirement" :fund="fund" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <StepModelRequirementsResultForm :fund="fund" />
        </v-col>

        <v-col cols="12">
          <v-dialog v-model="dialog" persistent max-width="600">
            <template #activator="{ on, attrs }">
              <v-btn
                v-if="showFinishButton"
                color="primary"
                class="mr-4"
                :disabled="!readyToFinish"
                v-bind="attrs"
                v-on="on"
              >
                {{ $t("auditor.stepModelRequirements.finish") }}
              </v-btn>
            </template>
            <v-card>
              <v-card-title class="headline">
                {{ $t("auditor.stepModelRequirements.confirmation.title") }}
              </v-card-title>
              <v-card-text>
                {{ $t("auditor.stepModelRequirements.confirmation.description") }}
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="warning" @click="dialog = false">
                  {{ $t("auditor.stepModelRequirements.confirmation.cancel") }}
                </v-btn>
                <v-btn color="primary" @click="finish" :loading="loading">
                  {{ $t("auditor.stepModelRequirements.confirmation.accept") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import { all, includes, flatten, map } from "ramda";

import AuditStatus from "@/components/Audit/Fund/AuditStatus";
import DataTable from "@/components/Fund/DataTable";
import groupByCategory from "@/utils/Requirements/groupByCategory";
import StepModelSuccess from "@/components/Audit/Fund/StepModelSuccess";
import StepModelRequirementsResultForm from "./StepModelRequirementsResultForm";
import StepModelRequirementForm from "@/components/Audit/Fund/StepModelRequirementForm";
import FundStepModelRequirementsQuery from "@/gql/queries/FundStepModelRequirementsQuery.gql";
import FinishAuditStepModelRequirementsMutation from "@/gql/mutations/FinishAuditStepModelRequirementsMutation.gql";
import generateReport from "@fng/common/report/generateReport";
import StepModelRequirementsSummaryTable from "@/components/User/Fund/Report/StepModelRequirementsSummaryTable.vue";
import CategoryPoints from "@/components/Audit/Fund/CategoryPoints";

const flattenStepModelCategories = (categories) =>
  flatten(
    map(
      (category) => (category.children.length ? [category, flattenStepModelCategories(category.children)] : category),
      categories
    )
  );

export default {
  components: {
    DataTable,
    AuditStatus,
    StepModelSuccess,
    StepModelRequirementForm,
    StepModelRequirementsResultForm,
    StepModelRequirementsSummaryTable,
    CategoryPoints,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      fund: null,
      dialog: false,
      loading: false,
    };
  },

  computed: {
    categories() {
      return groupByCategory(this.fund.stepModelRequirements);
    },

    awaitingEdit() {
      return (
        this.fund.state === "IN_PROGRESS" &&
        includes(this.fund.stepModelRequirementsState, ["INTERVIEW", "EDIT", "SECOND_EDIT", "FINAL_EDIT"])
      );
    },

    readyToFinish() {
      return all((req) => includes(req.state, ["DONE", "EDIT"]) && !req.wip, this.fund.stepModelRequirements);
    },

    showFinishButton() {
      return (
        this.$auth.may(["audit:finish"]) &&
        includes(this.fund.stepModelRequirementsState, ["AUDIT", "SECOND_AUDIT", "INTERVIEW_AUDIT"])
      );
    },

    report() {
      return generateReport(this.fund, [], this.fund.stepModelRequirements, true);
    },

    reportCategories() {
      return flattenStepModelCategories(this.report.stepModelCategories);
    },
  },

  apollo: {
    fund: {
      query: FundStepModelRequirementsQuery,
      fetchPolicy: "cache-and-network",
      variables() {
        return {
          id: this.id,
        };
      },
    },
  },

  methods: {
    async finish() {
      this.loading = true;

      await this.$apollo.mutate({
        mutation: FinishAuditStepModelRequirementsMutation,
        variables: {
          id: this.fund.id,
        },
      });

      this.dialog = false;
      this.loading = false;
    },
  },
};
</script>
